import React, { useState, useLayoutEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { httpClient } from 'src/helpers';
import { Page, InputField, Button } from 'src/components';
import countries from 'src/constants/countries.json';
import types from 'src/constants/types.json';
import interesets from 'src/constants/interesets.json';

import HeaderBanner from 'src/assets/images/stadium_banner.png';
import Section1Banner from 'src/assets/images/section1_banner.png';
import Check from 'src/assets/images/check.png';
import Badge from 'src/assets/images/stadium_badge.svg';
import CAAIcon from 'src/assets/images/caa_icon.png';
import OVG from 'src/assets/images/ovg.png';
import StateFarm from 'src/assets/images/State_farm.svg';
import Temoignage from 'src/assets/images/Temoignage.png';

import { useLocation } from '@reach/router';

const CONTACT_US_SCHEMA = Yup.object().shape({
  firstName: Yup.string().max(250, 'forms.too_long').required('required'),
  lastName: Yup.string().max(250, 'forms.too_long').required('required'),
  email: Yup.string().email('format').max(250, 'forms.too_long').required('required'),
  phone: Yup.string()
    .max(250, 'forms.too_long')
    .matches(
      /^([+(]?|\d{1}|(\+?\d{2}))[ ]?(\(?\d{1,4}\)?)[ ]?\d{1,4}[- ]?\d{1,4}([- ]?\d{1,4})?$/,
      'format'
    )
    .required('required'),
  company: Yup.string().max(250, 'forms.too_long').required('required'),
  country: Yup.string().max(250, 'forms.too_long').required('required'),
  role: Yup.string().max(250, 'forms.too_long').required('required'),
  capacity: Yup.number().min(1, 'seatsError').required('required'),
  message: Yup.string().max(250, 'forms.too_long').required('required'),
  type: Yup.string().max(250, 'forms.too_long').required('required'),
  interest: Yup.string().max(250, 'forms.too_long').required('required'),
});

const INITIAL_CONTACT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  country: 'US',
  role: '',
  capacity: 0,
  message: '',
  type: '',
  interest: '',
};

const Index = () => {
  const { t } = useTranslation(['venue', 'countries']);
  const [contactSuccess, setContactSuccess] = useState(null);

  const handleContact = async (values, { setSubmitting, resetForm }) => {
    if (window.gtag) window.gtag('event', 'Click_contact_us');
    setSubmitting(true);

    try {
      const { status } = await httpClient.post('/contact?origin=stadium', values);

      if (status === 200) {
        setSubmitting(false);
        setContactSuccess('SUCCESS');
        resetForm();
      } else {
        throw Error('Contact request failed.');
      }
    } catch (error) {
      setContactSuccess('ERROR');
      setSubmitting(false);
    }
  };

  const { hash } = useLocation();
  useLayoutEffect(() => {
    if (hash === '#contact-us') {
      window.scrollTo({
        top: document.getElementById('contact-us').offsetTop - 32,
        left: 0,
        behavior: 'auto',
      });
    }
  }, []);

  return (
    <Page>
      <Page.Header className="pt-16 bg-secondary-green-light">
        <div className="container">
          <div className="flex flex-col-reverse lg:flex-row lg:pb-24">
            <div className="items-center py-12 lg:flex-1 lg:flex lg:pt-12 lg:pb-0">
              <div className="max-w-xl">
                <p className="text-lg font-bold text-primary-dark tiempos">
                  {t('venue:header.title')}
                </p>
                <span className="text-5xl leading-10 tracking-wide text-primary-dark tiempos md:text-7xl">
                  {t('venue:header.tagline')}
                </span>
                <p className="pt-6 text-primary-dark">
                  {t('venue:header.introductions.first')}
                  <br />
                  <br />
                  {t('venue:header.introductions.second')}
                </p>
                <div className="flex pt-4">
                  <a
                    href="#contact-us"
                    className="w-full max-w-sm px-6 py-2 text-center text-white capitalize transition border-2 rounded-md border-primary-light bg-primary-light hover:bg-primary-light-hover hover:border-primary-light-hover focus:bg-primary-light-hover focus:border-primary-light-hover focus:outline-none"
                  >
                    {t('venue:buttons.getStarted')}
                  </a>
                </div>
              </div>
            </div>
            <div className="lg:flex-1 lg:block">
              <img src={HeaderBanner} alt="Sharecare | HealthSecurity" className="w-full" />
            </div>
          </div>
        </div>
        <div className="container pb-16 text-primary-grey">
          <div className="space-y-16 md:flex md:space-y-0 md:space-x-16">
            <div className="w-full">
              <div className="space-y-2 text-center">
                <h2 className="font-bold text-primary-dark tiempos">
                  {t('venue:header.bulle1.title')}
                </h2>
                <p className="lg:px-8">{t('venue:header.bulle1.description')}</p>
              </div>
            </div>
            <div className="w-full">
              <div className="space-y-2 text-center">
                <h2 className="font-bold text-primary-dark tiempos">
                  {t('venue:header.bulle2.title')}
                </h2>
                <p className="lg:px-8">{t('venue:header.bulle2.description')}</p>
              </div>
            </div>
            <div className="w-full">
              <div className="space-y-2 text-center">
                <h2 className="font-bold text-primary-dark tiempos">
                  {t('venue:header.bulle3.title')}
                </h2>
                <p className="lg:px-8">{t('venue:header.bulle3.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </Page.Header>
      <Page.Main className="text-primary-grey">
        <section className="container pt-16 pb-16">
          <div className="m-auto text-center">
            <span className="text-4xl text-primary-dark tiempos md:text-5xl">
              {t('venue:how.title')}
              <br />
              {t('venue:how.tagline')}
            </span>
          </div>
          <div className="flex flex-col items-center pt-8 space-y-12 md:flex-row md:space-y-0 md:space-x-6">
            <img
              src={Section1Banner}
              alt="Sharecare | HealthSecurity"
              className="object-cover w-full h-full md:w-1/2 md:h-auto"
            />
            <div className="space-y-4">
              <div className="flex space-x-4">
                <img src={Check} alt="Sharecare | HealthSecurity" className="h-full" />
                <span>{t('venue:how.items.chatbot')}</span>
              </div>
              <div className="flex space-x-4">
                <img src={Check} alt="Sharecare | HealthSecurity" className="h-full" />
                <span>{t('venue:how.items.protocols')}</span>
              </div>
              <div className="flex space-x-4">
                <img src={Check} alt="Sharecare | HealthSecurity" className="h-full" />
                <span>{t('venue:how.items.management')}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-secondary-green-light">
          <div className="container pt-16 pb-16">
            <div className="m-auto text-center">
              <span className="text-4xl text-primary-dark tiempos md:text-5xl">
                {t('venue:easy.title')}
              </span>
            </div>
            <p className="pt-8 m-auto text-center lg:w-1/2">{t('venue:easy.tagline')}</p>
            <div className="flex justify-center pt-8">
              <a
                href="#contact-us"
                className="w-full max-w-sm px-6 py-2 text-center text-white transition border-2 rounded-md border-primary-light bg-primary-light hover:bg-primary-light-hover hover:border-primary-light-hover focus:bg-primary-light-hover focus:border-primary-light-hover focus:outline-none"
              >
                {t('venue:buttons.getStarted')}
              </a>
            </div>
            <div className="pt-16 space-y-16 md:flex md:space-y-0 md:space-x-16">
              <div className="w-full">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <svg viewBox="0 0 58 58" className="h-16">
                    <path
                      d="M49.1778 53.4889H49.8222C51.2459 53.4889 52.4 54.643 52.4 56.0667V58H6V56.0667C6 54.643 7.15411 53.4889 8.57778 53.4889H9.22222V2.57778C9.22222 1.15411 10.3763 0 11.8 0H46.6C48.0237 0 49.1778 1.15411 49.1778 2.57778V53.4889ZM27.2667 42.5333C26.5548 42.5333 25.9778 43.1104 25.9778 43.8222V54.1333H32.4222V43.8222C32.4222 43.1104 31.8452 42.5333 31.1333 42.5333H27.2667ZM20.8222 9.66667C20.1104 9.66667 19.5333 10.2437 19.5333 10.9556V14.8222C19.5333 15.5341 20.1104 16.1111 20.8222 16.1111H24.6889C25.4007 16.1111 25.9778 15.5341 25.9778 14.8222V10.9556C25.9778 10.2437 25.4007 9.66667 24.6889 9.66667H20.8222ZM20.8222 20.6222C20.1104 20.6222 19.5333 21.1993 19.5333 21.9111V25.7778C19.5333 26.4896 20.1104 27.0667 20.8222 27.0667H24.6889C25.4007 27.0667 25.9778 26.4896 25.9778 25.7778V21.9111C25.9778 21.1993 25.4007 20.6222 24.6889 20.6222H20.8222ZM20.8222 31.5778C20.1104 31.5778 19.5333 32.1548 19.5333 32.8667V36.7333C19.5333 37.4452 20.1104 38.0222 20.8222 38.0222H24.6889C25.4007 38.0222 25.9778 37.4452 25.9778 36.7333V32.8667C25.9778 32.1548 25.4007 31.5778 24.6889 31.5778H20.8222ZM33.7111 31.5778C32.9993 31.5778 32.4222 32.1548 32.4222 32.8667V36.7333C32.4222 37.4452 32.9993 38.0222 33.7111 38.0222H37.5778C38.2896 38.0222 38.8667 37.4452 38.8667 36.7333V32.8667C38.8667 32.1548 38.2896 31.5778 37.5778 31.5778H33.7111ZM33.7111 20.6222C32.9993 20.6222 32.4222 21.1993 32.4222 21.9111V25.7778C32.4222 26.4896 32.9993 27.0667 33.7111 27.0667H37.5778C38.2896 27.0667 38.8667 26.4896 38.8667 25.7778V21.9111C38.8667 21.1993 38.2896 20.6222 37.5778 20.6222H33.7111ZM33.7111 9.66667C32.9993 9.66667 32.4222 10.2437 32.4222 10.9556V14.8222C32.4222 15.5341 32.9993 16.1111 33.7111 16.1111H37.5778C38.2896 16.1111 38.8667 15.5341 38.8667 14.8222V10.9556C38.8667 10.2437 38.2896 9.66667 37.5778 9.66667H33.7111Z"
                      fill="#0D5547"
                    />
                  </svg>

                  <div className="space-y-2 text-center md:pt-4">
                    <p className="text-2xl font-bold text-primary-dark tiempos">
                      {t('venue:easy.items.facility.title')}
                    </p>
                    <p className="lg:px-8">{t('venue:easy.items.facility.description')}</p>
                  </div>
                </div>
              </div>
              <div className="w-full">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <svg viewBox="0 0 58 58" className="w-16 md:h-16">
                    <path
                      d="M40.4168 52.219H4.27368V8.74955H9.82947V10.5811C9.82947 11.7411 10.8063 12.718 11.9663 12.718C13.1263 12.718 14.1032 11.7411 14.1032 10.5811V8.74955H20.2695V10.5811C20.2695 11.7411 21.2463 12.718 22.4063 12.718C23.5663 12.718 24.5432 11.7411 24.5432 10.5811V8.74955H30.7095V10.5811C30.7095 11.7411 31.6863 12.718 32.8463 12.718C34.0063 12.718 34.9832 11.7411 34.9832 10.5811V8.74955H40.5389V29.2022L44.8126 24.9285V7.71166C44.8126 5.94113 43.4084 4.47587 41.5768 4.47587H34.9221V2.64429C34.9221 1.48429 33.9453 0.507446 32.7853 0.507446C31.6253 0.507446 30.6484 1.48429 30.6484 2.64429V4.47587H24.4821V2.64429C24.4821 1.48429 23.5053 0.507446 22.3453 0.507446C21.1853 0.507446 20.2084 1.48429 20.2084 2.64429V4.47587H14.0421V2.64429C14.0421 1.48429 13.0653 0.507446 11.9053 0.507446C10.7453 0.507446 9.76842 1.48429 9.76842 2.64429V4.47587H3.23579C1.46526 4.47587 0 5.88008 0 7.71166V53.2569C0 55.0274 1.40421 56.4927 3.23579 56.4927H41.5158C43.2863 56.4927 44.7516 55.0274 44.7516 53.2569V44.1601L40.4779 48.4338V52.219H40.4168Z"
                      fill="#0D5547"
                    />
                    <path
                      d="M34.2507 32.0106C34.2507 30.8506 33.2739 29.8738 32.1139 29.8738H12.6381C11.4781 29.8738 10.5012 30.8506 10.5012 32.0106C10.5012 33.1706 11.4781 34.1475 12.6381 34.1475H32.1139C33.2739 34.1475 34.2507 33.1706 34.2507 32.0106Z"
                      fill="#0D5547"
                    />
                    <path
                      d="M12.6381 23.4633H32.1139C33.2739 23.4633 34.2507 22.4864 34.2507 21.3264C34.2507 20.1664 33.2739 19.1896 32.1139 19.1896H12.6381C11.4781 19.1896 10.5012 20.1664 10.5012 21.3264C10.5012 22.4864 11.417 23.4633 12.6381 23.4633Z"
                      fill="#0D5547"
                    />
                    <path
                      d="M10.5012 42.6948C10.5012 43.9159 11.417 44.8317 12.6381 44.8317H28.6339L29.4886 41.0464C29.5496 40.8632 29.5496 40.6801 29.6107 40.558H12.6381C11.417 40.558 10.5012 41.5348 10.5012 42.6948Z"
                      fill="#0D5547"
                    />
                    <path
                      d="M32.7854 47.7622L37.4864 46.7243C37.6696 46.6632 37.8527 46.5411 38.0359 46.419L39.4401 44.9538L34.2506 39.7643L32.8464 41.1685C32.6633 41.3516 32.6022 41.4738 32.5412 41.718L31.4422 46.4801C31.2591 47.2738 31.9917 47.9453 32.7854 47.7622Z"
                      fill="#0D5547"
                    />
                    <path
                      d="M36.0823 37.9937L41.2107 43.1832L53.4212 30.9727L48.2928 25.7832L36.0823 37.9937Z"
                      fill="#0D5547"
                    />
                    <path
                      d="M57.6335 25.2338L54.0314 21.5706C53.543 21.1433 52.8714 21.1433 52.4441 21.5706L50.002 24.0128L55.1914 29.2022L57.6335 26.7601C58.122 26.3328 58.122 25.6612 57.6335 25.2338Z"
                      fill="#0D5547"
                    />
                  </svg>

                  <div className="space-y-2 text-center md:pt-4">
                    <p className="text-2xl font-bold text-primary-dark tiempos">
                      {t('venue:easy.items.assessment.title')}
                    </p>
                    <p className="lg:px-8">{t('venue:easy.items.assessment.description')}</p>
                  </div>
                </div>
              </div>
              <div className="w-full">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <svg viewBox="0 0 58 58" className="w-16 md:h-16">
                    <path
                      d="M21.6992 33.8581L17.6081 29.7031L21.8909 25.4203L25.982 29.5753L35.8261 19.7951L40.1089 24.078L30.2648 33.922L25.982 38.2049L21.6992 33.8581ZM0.924305 29.5114L1.94707 28.3608C3.03375 27.1462 3.22552 25.3564 2.39453 23.9501L1.62745 22.6717C0.476846 20.6901 1.37176 18.1971 3.48121 17.3022L4.82359 16.7268C6.29381 16.0876 7.25265 14.6174 7.1248 12.9554L6.99696 11.4852C6.86911 9.18398 8.72287 7.33022 10.9602 7.45806L12.4304 7.52199C14.0284 7.58591 15.5626 6.62707 16.1379 5.15685L16.7132 3.75055C17.5442 1.6411 20.0372 0.746185 22.0827 1.83287L23.3612 2.53602C24.7675 3.30309 26.5573 3.11132 27.7718 2.02464L28.8585 1.00188C30.5205 -0.532268 33.2052 -0.276577 34.4837 1.57718L35.3786 2.79171C36.3375 4.13409 37.9994 4.70939 39.5336 4.19801L40.9399 3.75055C43.1133 3.11132 45.3506 4.51762 45.7341 6.75491L45.9898 8.22514C46.2455 9.8232 47.5239 11.1017 49.122 11.2934L50.5922 11.5491C52.8295 11.8687 54.2997 14.1699 53.6605 16.3433L53.277 17.7496C52.8295 19.2838 53.4048 21.0097 54.7472 21.9046L55.9617 22.7356C57.8155 24.014 58.1351 26.6988 56.6649 28.3608L55.7699 29.6392C54.6833 30.8538 54.4915 32.6436 55.3225 34.0499L56.0896 35.3283C57.2402 37.3099 56.3452 39.8029 54.2358 40.6978L52.8934 41.2731C51.4232 41.9124 50.4644 43.3826 50.5922 45.0446L50.7201 46.5148C50.8479 48.816 48.9941 50.6698 46.7568 50.5419L45.2866 50.478C43.6886 50.4141 42.1544 51.3729 41.5791 52.8432L41.0038 54.2494C40.1728 56.3589 37.6798 57.2538 35.6343 56.1671L34.3559 55.464C32.9496 54.6969 31.1597 54.8887 29.9452 55.9754L28.8585 56.9981C27.1965 58.5323 24.5118 58.2766 23.2333 56.4228L22.3384 55.2083C21.3796 53.8659 19.7176 53.2906 18.1834 53.802L16.7771 54.2494C14.6038 54.8887 12.3665 53.4824 11.9829 51.2451L11.5994 49.647C11.3437 48.049 10.0652 46.7705 8.46718 46.5787L6.99696 46.323C4.75966 46.0034 3.28944 43.7022 3.92867 41.5288L4.31221 40.1225C4.75966 38.5884 4.18436 36.8625 2.84198 35.9676L1.62745 35.1366C-0.226303 33.8581 -0.545916 31.1734 0.924305 29.5114ZM10.8962 29C10.8962 38.908 18.9505 46.9623 28.8585 46.9623C38.7665 46.9623 46.8208 38.908 46.8208 29C46.8208 19.092 38.7665 11.0377 28.8585 11.0377C18.9505 11.0377 10.8962 19.092 10.8962 29Z"
                      fill="#0D5547"
                    />
                  </svg>

                  <div className="space-y-2 text-center md:pt-4">
                    <p className="text-2xl font-bold text-primary-dark tiempos">
                      {t('venue:easy.items.badge.title')}
                    </p>
                    <p className="lg:px-8">{t('venue:easy.items.badge.description')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="container flex flex-col items-center py-16 lg:space-x-12 lg:flex-row">
          <div className="border border-gray-400 rounded-lg shadow-lg">
            <div className="p-4 sm:w-80">
              <img className="h-48 m-auto" src={Badge} alt="Sharecare | HealthSecurity" />
              <p className="pt-2 m-auto text-center">{t('venue:badge.card')}</p>
            </div>
          </div>
          <div className="flex flex-col pt-12">
            <span className="text-lg font-bold text-primary-dark">{t('venue:badge.tag')}</span>
            <span className="text-4xl text-primary-dark tiempos md:text-5xl">
              {t('venue:badge.title')}
            </span>
            <p className="max-w-screen-md pt-6">
              {t('venue:badge.description')}
              <br />
              <br />
              {t('venue:badge.pricing')}
              <a
                href="#contact-us"
                className="pl-1 underline transition text-primary-light hover:text-primary-dark focus:outline-none"
              >
                {`${t('venue:buttons.contactUs')}.`}
              </a>
            </p>
          </div>
        </section>

        <section className="container py-16 text-center">
          <h4 className="text-4xl lg:px-64 text-primary-dark tiempos md:text-5xl">
            {t('venue:stages.title')}
          </h4>
          <p className="max-w-screen-md pt-8 m-auto text-center">{t('venue:stages.description')}</p>
          <div className="pt-4 lg:flex lg:space-x-4">
            <div className="w-full">
              <div className="flex flex-col justify-center m-auto mt-8 ml-0 rounded-lg shadow-lg h-28 lg:h-44 lg:pt-0 lg:m-none">
                <span className="w-32 mx-auto font-bold text-center">
                  {t('venue:stages.items.1.title')}
                </span>
                <span className="w-32 pt-2 mx-auto text-xs text-center lg:pt-4 text-secondary">
                  {t('venue:stages.items.1.description')}
                </span>
              </div>
            </div>
            <div className="w-full">
              <div className="flex flex-col justify-center mt-8 ml-0 rounded-lg shadow-lg h-28 lg:h-44 lg:pt-0 lg:m-none">
                <span className="w-32 px-4 mx-auto font-bold text-center">
                  {t('venue:stages.items.2.title')}
                </span>
                <span className="w-32 pt-2 mx-auto text-xs text-center lg:pt-4 text-secondary">
                  {t('venue:stages.items.2.description')}
                </span>
              </div>
            </div>
            <div className="w-full">
              <div className="flex flex-col justify-center mt-8 ml-0 rounded-lg shadow-lg h-28 lg:h-44 lg:pt-0 lg:m-none">
                <span className="w-32 mx-auto font-bold text-center lg:px-4">
                  {t('venue:stages.items.3.title')}
                </span>
                <span className="w-32 pt-2 mx-auto text-xs text-center lg:pt-4 text-secondary">
                  {t('venue:stages.items.3.description')}
                </span>
              </div>
            </div>
            <div className="w-full">
              <div className="flex flex-col justify-center mt-8 ml-0 rounded-lg shadow-lg h-28 lg:h-44 lg:pt-0 lg:m-none">
                <span className="w-32 mx-auto font-bold text-center">
                  {t('venue:stages.items.4.title')}
                </span>
                <span className="w-32 pt-2 mx-auto text-xs text-center lg:pt-4 text-secondary">
                  {t('venue:stages.items.4.description')}
                </span>
              </div>
            </div>
            <div className="w-full">
              <div className="flex flex-col justify-center mt-8 ml-0 rounded-lg shadow-lg h-28 lg:h-44 lg:pt-0 lg:m-none">
                <span className="w-32 mx-auto font-bold text-center line">
                  {t('venue:stages.items.5.title')}
                </span>
                <span className="w-32 pt-2 mx-auto text-xs text-center lg:pt-4 text-secondary">
                  {t('venue:stages.items.5.description')}
                </span>
              </div>
            </div>
            <div className="w-full">
              <div className="flex flex-col justify-center mt-8 ml-0 rounded-lg shadow-lg h-28 lg:h-44 lg:pt-0 lg:m-none">
                <span className="w-32 mx-auto font-bold text-center">
                  {t('venue:stages.items.6.title')}
                </span>
                <span className="w-32 pt-2 mx-auto text-xs text-center lg:pt-4 text-secondary">
                  {t('venue:stages.items.6.description')}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <p className="pt-16 text-justify">{t('venue:stages.more')}</p>
            <div className="flex pt-4">
              <a
                href="https://www.forbestravelguide.com/verified-stadiums-venues"
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 transition border-2 border-gray-300 rounded-md hover:bg-secondary-grey-light focus:bg-secondary-grey-light focus:outline-none"
              >
                {t('venue:stages.moreButton')}
              </a>
            </div>
          </div>
        </section>

        <section className="container pt-16 pb-16 text-center">
          <h4 className="text-4xl text-primary-dark tiempos md:text-5xl">
            {t('venue:unique.title')}
          </h4>
          <div className="pt-4 pb-8 lg:grid lg:grid-cols-3">
            <div className="lg:pr-8">
              <div className="flex flex-col justify-center h-32 mt-8 ml-0 rounded-lg shadow-lg lg:pt-0 lg:m-none">
                <span className="px-4 pt-8 font-bold text-center lg:text-left">
                  {t('venue:unique.items.1.title')}
                </span>
                <span className="px-4 pt-2 pb-8 text-sm text-center lg:text-left text-secondary">
                  {t('venue:unique.items.1.description')}
                </span>
              </div>
            </div>
            <div className="lg:pr-8">
              <div className="flex flex-col justify-center h-32 mt-8 ml-0 rounded-lg shadow-lg lg:pt-0 lg:m-none">
                <span className="px-4 pt-8 font-bold text-center lg:text-left">
                  {t('venue:unique.items.2.title')}
                </span>
                <span className="px-4 pt-2 pb-8 text-sm text-center lg:text-left text-secondary">
                  {t('venue:unique.items.2.description')}
                </span>
              </div>
            </div>
            <div className="">
              <div className="flex flex-col justify-center h-32 mt-8 ml-0 rounded-lg shadow-lg lg:pt-0 lg:m-none">
                <span className="px-4 pt-8 font-bold text-center lg:text-left">
                  {t('venue:unique.items.3.title')}
                </span>
                <span className="px-4 pt-2 pb-8 text-sm text-center lg:text-left text-secondary">
                  {t('venue:unique.items.3.description')}
                </span>
              </div>
            </div>
            <div className="lg:pr-8">
              <div className="flex flex-col justify-center h-32 mt-8 ml-0 rounded-lg shadow-lg lg:pt-0 lg:m-none">
                <span className="px-4 pt-8 font-bold text-center lg:text-left">
                  {t('venue:unique.items.4.title')}
                </span>
                <span className="px-4 pt-2 pb-8 text-sm text-center lg:text-left text-secondary">
                  {t('venue:unique.items.4.description')}
                </span>
              </div>
            </div>
            <div className="lg:pr-8">
              <div className="flex flex-col justify-center h-32 mt-8 ml-0 rounded-lg shadow-lg lg:pt-0 lg:m-none">
                <span className="px-4 pt-8 font-bold text-center lg:text-left">
                  {t('venue:unique.items.5.title')}
                </span>
                <span className="px-4 pt-2 pb-8 text-sm text-center lg:text-left text-secondary">
                  {t('venue:unique.items.5.description')}
                </span>
              </div>
            </div>
            <div className="">
              <div className="flex flex-col justify-center h-32 mt-8 ml-0 rounded-lg shadow-lg lg:pt-0 lg:m-none">
                <span className="px-4 pt-8 font-bold text-center lg:text-left">
                  {t('venue:unique.items.6.title')}
                </span>
                <span className="px-4 pt-2 pb-8 text-sm text-center lg:text-left text-secondary">
                  {t('venue:unique.items.6.description')}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="container py-16">
          <div className="flex flex-col items-center justify-center space-y-6">
            <span className="text-4xl text-center text-primary-dark tiempos md:text-5xl">
              {t('venue:endorsed.title')}
            </span>
            <div className="grid grid-cols-1 pt-12 md:grid-cols-2">
              <img
                src={CAAIcon}
                alt="Sharecare | HealthSecurity"
                className="m-auto lg:pr-10 lg:max-w-xs"
              />
              <img
                src={OVG}
                alt="Sharecare | HealthSecurity"
                className="pt-12 m-auto lg:pl-10 lg:pt-0 lg:max-w-xs"
              />
            </div>
          </div>
        </section>

        <section className="pt-16 pb-24">
          <div className="container py-8 rounded-lg shadow-lg lg:flex lg:w-4/5 xl:w-2/3 lg:m-auto bg-secondary-grey-light">
            <img className="m-auto lg:flex w-30" src={StateFarm} alt="Sharecare | HealthSecurity" />
            <div className="flex lg:pl-6 lg:pr-6">
              <div className="flex-col">
                <p className="text-justify">{t('venue:testimonial.testi')}</p>
                <p className="pt-8 font-bold text-primary-dark">{t('venue:testimonial.author')}</p>
                <p className="font-bold text-primary-light">{t('venue:testimonial.title')}</p>
              </div>
              <img
                className="flex h-8 pl-6 lg:pl-6 lg:pr-6"
                src={Temoignage}
                alt="Sharecare | HealthSecurity"
              />
            </div>
          </div>
        </section>
        <section id="contact-us" name="contact-us" className="relative">
          <div
            className="absolute inset-0 bg-secondary-green-light xl:my-24 2xl:my-48"
            style={{ zIndex: -1 }}
          />
          <div className="container max-w-full p-0 xl:max-w-screen-xl 2xl:max-w-screen-2xl xl:px-8">
            <div className="flex flex-col xl:flex-row xl:items-center">
              <div className="flex flex-col px-8 py-12 lg:items-center xl:pr-12">
                <div className="flex flex-col space-y-6 lg:max-w-screen-sm">
                  <span className="text-4xl text-center text-primary-dark tiempos md:text-5xl xl:hidden">
                    {t('venue:contact.title')}
                  </span>
                  <p>
                    {t('venue:contact.introductions.first')}
                    <br />
                    <br />
                    {t('venue:contact.introductions.second')}
                  </p>
                </div>
              </div>
              <div className="px-8 py-12 bg-white xl:border xl:rounded">
                <Formik
                  initialValues={INITIAL_CONTACT_VALUES}
                  validationSchema={CONTACT_US_SCHEMA}
                  onSubmit={handleContact}
                >
                  {({ isValid, isSubmitting, dirty }) => (
                    <Form className="space-y-6">
                      <span className="hidden text-4xl text-center text-primary-dark tiempos md:text-5xl xl:block">
                        {t('venue:contact.title')}
                      </span>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <InputField
                          name="firstName"
                          label="contact.firstName"
                          autoComplete="given-name"
                          namespaces={['venue']}
                        />
                        <InputField
                          name="lastName"
                          label="contact.lastName"
                          autoComplete="family-name"
                          namespaces={['venue']}
                        />
                        <InputField
                          name="email"
                          label="contact.email"
                          autoComplete="email"
                          namespaces={['venue']}
                        />
                        <InputField
                          name="phone"
                          label="contact.phone"
                          autoComplete="phone"
                          namespaces={['venue']}
                        />
                        <InputField
                          name="company"
                          label="contact.company"
                          autoComplete="company"
                          namespaces={['venue']}
                        />
                        <InputField
                          type="select"
                          name="country"
                          label="contact.country"
                          namespaces={['venue']}
                          options={countries
                            .map((country) => ({
                              ...country,
                              label: t(`countries:${country.label}`),
                            }))
                            .sort((a, b) =>
                              t(`countries:${a.label}`).localeCompare(t(`countries:${b.label}`))
                            )}
                        />
                        <InputField name="role" label="contact.role" namespaces={['venue']} />
                        <InputField
                          type="number"
                          name="capacity"
                          label="contact.capacity"
                          namespaces={['venue']}
                        />
                        <InputField
                          type="select"
                          name="type"
                          label="contact.type"
                          namespaces={['venue']}
                          options={types.map((type) => ({
                            ...type,
                            label: t(type.label),
                          }))}
                        />
                        <InputField
                          type="select"
                          name="interest"
                          label="contact.interest"
                          namespaces={['venue']}
                          options={interesets.map((interest) => ({
                            ...interest,
                            label: t(interest.label),
                          }))}
                        />
                        <div className="col-span-1 md:col-span-2">
                          <InputField
                            type="textarea"
                            name="message"
                            label="contact.message"
                            namespaces={['venue']}
                          />
                        </div>
                      </div>
                      <div className="flex justify-center">
                        <div className="w-full max-w-sm">
                          <Button
                            type="submit"
                            disabled={!(isValid && dirty) || isSubmitting}
                            loading={isSubmitting}
                          >
                            {t('venue:contact.submit')}
                          </Button>
                        </div>
                      </div>
                      {contactSuccess && (
                        <div>
                          {contactSuccess === 'SUCCESS' && (
                            <div className="px-6 py-4 text-gray-800 rounded-md bg-primary-light">
                              Your message has successfully been sent. Someone from our team will
                              contact you within 1 to 2 business days.
                            </div>
                          )}
                          {contactSuccess === 'ERROR' && (
                            <div className="px-6 py-4 text-white bg-red-500 rounded-md">
                              Something went wrong. Please try again later.
                            </div>
                          )}
                        </div>
                      )}
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </section>
      </Page.Main>
    </Page>
  );
};

export default Index;
